/**
 * Boomfection Sound Manager
 * Handles all sound effects for the game with Web Audio API
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

// Debug-Konfiguration (false für Production)
const DEBUG_SOUND = false;
const log = DEBUG_SOUND ? console.log.bind(console) : () => {};

/**
 * Sound definitions with paths and default volumes
 */
export const SOUNDS = {
    // Game State
    gameStart: { path: './sounds/game-start.mp3', volume: 0.8 },
    victory: { path: './sounds/victory.mp3', volume: 1.0 },
    defeat: { path: './sounds/defeat.mp3', volume: 0.8 },

    // Gameplay - Explosions & Chain Reactions (eskalierend 1-5)
    explosion1: { path: './sounds/explosion.mp3', volume: 0.6 },
    explosion2: { path: './sounds/explosion2.mp3', volume: 0.65 },
    explosion3: { path: './sounds/explosion3.mp3', volume: 0.7 },
    explosion4: { path: './sounds/explosion4.mp3', volume: 0.75 },
    explosion5: { path: './sounds/explosion5.mp3', volume: 0.8 },
    chainReaction: { path: './sounds/chain-reaction.mp3', volume: 0.6 },
    criticalMass: { path: './sounds/critical-mass.mp3', volume: 0.5 },

    // Virus Actions
    placeVirus: { path: './sounds/place-virus.mp3', volume: 0.5 },
    fieldIncrease: { path: './sounds/field-increase.mp3', volume: 0.4 },
    fieldCaptured: { path: './sounds/field-captured.mp3', volume: 0.6 },
    fieldLost: { path: './sounds/field-lost.mp3', volume: 0.5 },

    // Turn Management
    yourTurn: { path: './sounds/your-turn.mp3', volume: 0.8 },
    turnWarning: { path: './sounds/turn-warning.mp3', volume: 0.7 },
    turnTimeout: { path: './sounds/turn-timeout.mp3', volume: 0.6 },

    // Multiplayer Events
    playerJoined: { path: './sounds/player-joined.mp3', volume: 0.5 },
    playerLeft: { path: './sounds/player-left.mp3', volume: 0.4 },
    playerEliminated: { path: './sounds/player-eliminated.mp3', volume: 0.7 },

    // Pills & Power-Ups
    pillSpawned: { path: './sounds/pill-spawned.mp3', volume: 0.5 },
    pillCollected: { path: './sounds/pill-collected.mp3', volume: 0.6 },

    // UI Sounds
    buttonClick: { path: './sounds/button-click.mp3', volume: 0.3 },
    buttonHover: { path: './sounds/button-hover.mp3', volume: 0.15 },
    menuOpen: { path: './sounds/menu-open.mp3', volume: 0.4 },
    error: { path: './sounds/error.mp3', volume: 0.5 },
    success: { path: './sounds/success.mp3', volume: 0.5 },

    // Chat
    chatMessage: { path: './sounds/chat-message.mp3', volume: 0.3 },
    quickChatGG: { path: './sounds/quickchat-gg.mp3', volume: 0.4 }
};

/**
 * Sound presets for different audio styles
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
export const SOUND_PRESETS = {
    default: { waveform: 'sine', filterFreq: 2000 },
    retro: { waveform: 'square', filterFreq: 800 },
    scifi: { waveform: 'sawtooth', filterFreq: 4000 },
    minimal: { waveform: 'triangle', filterFreq: 1000 }
};

/**
 * SoundManager Class
 * Manages loading, caching and playing sound effects using Web Audio API
 */
export class SoundManager {
    constructor() {
        /** @type {Map<string, AudioBuffer>} Cached sound buffers */
        this.sounds = new Map();

        /** @type {AudioContext|null} Web Audio API context */
        this.audioContext = null;

        /** @type {GainNode|null} Master gain node */
        this.masterGain = null;

        /** @type {boolean} Whether sound is enabled */
        this.enabled = true;

        /** @type {number} Master volume (0-1) */
        this.volume = 0.5;

        /** @type {boolean} Whether audio context has been initialized */
        this.initialized = false;

        /** @type {Set<string>} Currently loading sounds */
        this.loading = new Set();

        /** @type {Map<string, AudioBufferSourceNode>} Currently playing sources */
        this.activeSources = new Map();

        /** @type {string} Active sound preset name */
        this.presetName = 'default';

        /** @type {Object} Active preset config */
        this.preset = SOUND_PRESETS.default;

        /** @type {BiquadFilterNode|null} Master filter node */
        this.masterFilter = null;

        // Storage key for persisting settings
        this.STORAGE_KEY = 'boomfection_sound_settings';

        // Load saved settings
        this.loadSettings();
    }

    /**
     * Initialize AudioContext (must be called after user interaction)
     * @returns {boolean} Whether initialization was successful
     */
    init() {
        if (this.initialized) {
            return true;
        }

        try {
            // Create AudioContext
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) {
                console.warn('[Sound] Web Audio API not supported');
                return false;
            }

            this.audioContext = new AudioContextClass();

            // Create master filter node (for sound presets)
            this.masterFilter = this.audioContext.createBiquadFilter();
            this.masterFilter.type = 'lowpass';
            this.masterFilter.frequency.value = this.preset.filterFreq;
            this.masterFilter.connect(this.audioContext.destination);

            // Create master gain node
            this.masterGain = this.audioContext.createGain();
            this.masterGain.gain.value = this.volume;
            this.masterGain.connect(this.masterFilter);

            // Resume context if suspended (browser autoplay policy)
            if (this.audioContext.state === 'suspended') {
                this.audioContext.resume();
            }

            this.initialized = true;
            log('[Sound] AudioContext initialized');

            return true;
        } catch (err) {
            console.error('[Sound] Failed to initialize AudioContext:', err);
            return false;
        }
    }

    /**
     * Preload all sound definitions
     * @param {Object} soundDefinitions - Sound definitions object
     * @returns {Promise<void>}
     */
    async preload(soundDefinitions = SOUNDS) {
        if (!this.initialized) {
            console.warn('[Sound] Cannot preload - not initialized');
            return;
        }

        const loadPromises = Object.entries(soundDefinitions).map(([id, config]) => this.loadSound(id, config.path));

        try {
            await Promise.all(loadPromises);
            log(`[Sound] Preloaded ${this.sounds.size} sounds`);
        } catch (err) {
            console.warn('[Sound] Some sounds failed to load:', err);
        }
    }

    /**
     * Load a single sound file
     * @param {string} id - Sound identifier
     * @param {string} url - Sound file URL
     * @returns {Promise<AudioBuffer|null>}
     */
    async loadSound(id, url) {
        if (this.sounds.has(id)) {
            return this.sounds.get(id);
        }

        if (this.loading.has(id)) {
            return null;
        }

        this.loading.add(id);

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

            this.sounds.set(id, audioBuffer);
            log(`[Sound] Loaded: ${id}`);
            return audioBuffer;
        } catch (err) {
            console.warn(`[Sound] Failed to load ${id}:`, err.message);
            return null;
        } finally {
            this.loading.delete(id);
        }
    }

    /**
     * Play a sound by ID
     * @param {string} soundId - Sound identifier from SOUNDS
     * @param {Object} options - Playback options
     * @param {number} [options.volume=1] - Volume multiplier (0-1)
     * @param {boolean} [options.loop=false] - Whether to loop the sound
     * @param {number} [options.delay=0] - Delay before playing (seconds)
     * @param {number} [options.playbackRate=1] - Playback rate (1 = normal)
     * @returns {AudioBufferSourceNode|null} The audio source node, or null if failed
     */
    play(soundId, options = {}) {
        if (!this.enabled || !this.initialized || !this.audioContext) {
            return null;
        }

        const buffer = this.sounds.get(soundId);
        if (!buffer) {
            console.warn(`[Sound] Sound not loaded: ${soundId}`);
            return null;
        }

        // Resume context if suspended
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        try {
            // Create source node
            const source = this.audioContext.createBufferSource();
            source.buffer = buffer;

            // Create gain node for this sound
            const gainNode = this.audioContext.createGain();

            // Get default volume from SOUNDS config
            const soundConfig = SOUNDS[soundId];
            const defaultVolume = soundConfig?.volume ?? 1;
            const optionsVolume = options.volume ?? 1;

            gainNode.gain.value = defaultVolume * optionsVolume;

            // Apply playback options
            source.loop = options.loop || false;
            source.playbackRate.value = options.playbackRate || 1;

            // Connect nodes: source -> gain -> master -> destination
            source.connect(gainNode);
            gainNode.connect(this.masterGain);

            // Start playback
            const startTime = this.audioContext.currentTime + (options.delay || 0);
            source.start(startTime);

            // Track active source
            const sourceId = `${soundId}_${Date.now()}`;
            this.activeSources.set(sourceId, source);

            // Clean up when ended
            source.onended = () => {
                this.activeSources.delete(sourceId);
            };

            return source;
        } catch (err) {
            console.error(`[Sound] Failed to play ${soundId}:`, err);
            return null;
        }
    }

    /**
     * Play a sound with random pitch variation (for variety)
     * @param {string} soundId - Sound identifier
     * @param {number} [variation=0.1] - Pitch variation range (0-1)
     * @param {Object} [options={}] - Additional playback options
     * @returns {AudioBufferSourceNode|null}
     */
    playWithVariation(soundId, variation = 0.1, options = {}) {
        const rate = 1 + (Math.random() - 0.5) * variation * 2;
        return this.play(soundId, { ...options, playbackRate: rate });
    }

    /**
     * Play escalating explosion sound based on chain position
     * Sound escalates from 1→2→3→4→5 within a chain, then stays at 5
     * @param {number} chainPosition - Position in the chain (1-based)
     * @param {Object} [options={}] - Additional playback options
     * @returns {AudioBufferSourceNode|null}
     */
    playExplosion(chainPosition = 1, options = {}) {
        // Eskalation: 1→2→3→4→5→5→5...
        const soundLevel = Math.min(chainPosition, 5);
        const soundId = `explosion${soundLevel}`;

        // Leichte Pitch-Variation für mehr Abwechslung
        const variation = 0.08;
        const rate = 1 + (Math.random() - 0.5) * variation * 2;

        return this.play(soundId, { ...options, playbackRate: rate });
    }

    /**
     * Play escalating explosion sound for chain reactions with pitch/volume escalation
     * Pitch decreases and volume increases with each step in the chain.
     * At chain >= 5, an additional bass layer is synthesized.
     * At chain >= 10, a convolver reverb effect is applied.
     * @param {number} chainIndex - 0-based chain position
     * @param {Object} [options={}] - Additional playback options
     * @returns {AudioBufferSourceNode|null}
     * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
     */
    playExplosionChain(chainIndex = 0, options = {}) {
        const pitch = Math.max(0.5, 1.0 - chainIndex * 0.05);
        const volume = Math.min(1.0, 0.7 + chainIndex * 0.08);

        // Select escalating explosion sound (1-5)
        const soundLevel = Math.min(chainIndex + 1, 5);
        const soundId = `explosion${soundLevel}`;

        const source = this.play(soundId, { ...options, playbackRate: pitch, volume });

        // Bass layer at chain >= 5 (synthesized low-frequency rumble)
        if (chainIndex >= 5 && this.audioContext && this.initialized) {
            try {
                const ctx = this.audioContext;
                const now = ctx.currentTime;

                const bassOsc = ctx.createOscillator();
                const bassGain = ctx.createGain();
                bassOsc.type = 'sine';
                bassOsc.frequency.value = 40 + Math.random() * 20;
                bassGain.gain.setValueAtTime(0.3 * this.volume, now);
                bassGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
                bassOsc.connect(bassGain);
                bassGain.connect(this.masterGain);
                bassOsc.start(now);
                bassOsc.stop(now + 0.4);
            } catch {
                /* non-critical — bass synthesis failure does not affect gameplay */
            }
        }

        // Reverb effect at chain >= 10 (convolver simulation via delayed echoes)
        if (chainIndex >= 10 && this.audioContext && this.initialized) {
            try {
                const ctx = this.audioContext;
                const now = ctx.currentTime;

                // Create short echo layers for reverb-like effect
                const delays = [0.05, 0.1, 0.18];
                const gains = [0.15, 0.08, 0.04];

                for (let i = 0; i < delays.length; i++) {
                    const delayNode = ctx.createDelay(0.5);
                    delayNode.delayTime.value = delays[i];
                    const echoGain = ctx.createGain();
                    echoGain.gain.value = gains[i] * this.volume;

                    // Re-play the same buffer with delay
                    const buffer = this.sounds.get(soundId);
                    if (buffer) {
                        const echoSource = ctx.createBufferSource();
                        echoSource.buffer = buffer;
                        echoSource.playbackRate.value = pitch;
                        echoSource.connect(delayNode);
                        delayNode.connect(echoGain);
                        echoGain.connect(this.masterGain);
                        echoSource.start(now);
                        this.activeSources.push(echoSource);
                        echoSource.onended = () => {
                            const idx = this.activeSources.indexOf(echoSource);
                            if (idx !== -1) this.activeSources.splice(idx, 1);
                        };
                    }
                }
            } catch {
                /* non-critical — reverb synthesis failure does not affect gameplay */
            }
        }

        return source;
    }

    /**
     * Stop a specific playing sound
     * @param {AudioBufferSourceNode} source - The source node to stop
     */
    stop(source) {
        if (source) {
            try {
                source.stop();
            } catch {
                /* expected — source node may already be stopped or disconnected */
            }
        }
    }

    /**
     * Stop all currently playing sounds
     */
    stopAll() {
        for (const source of this.activeSources.values()) {
            try {
                source.stop();
            } catch {
                /* expected — source may already be stopped */
            }
        }
        this.activeSources.clear();
    }

    /**
     * Set master volume
     * @param {number} volume - Volume level (0-1)
     */
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(1, volume));
        if (this.masterGain) {
            this.masterGain.gain.value = this.volume;
        }
        this.saveSettings();
    }

    /**
     * Get current volume
     * @returns {number}
     */
    getVolume() {
        return this.volume;
    }

    /**
     * Enable or disable sound
     * @param {boolean} enabled
     */
    setEnabled(enabled) {
        this.enabled = enabled;
        if (!enabled) {
            this.stopAll();
        }
        this.saveSettings();
    }

    /**
     * Toggle sound on/off
     * @returns {boolean} New enabled state
     */
    toggle() {
        this.setEnabled(!this.enabled);
        return this.enabled;
    }

    /**
     * Check if sound is enabled
     * @returns {boolean}
     */
    isEnabled() {
        return this.enabled;
    }

    /**
     * Set sound preset that affects filter and synthesized sounds
     * @param {string} presetName - Preset name from SOUND_PRESETS
     * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
     */
    setPreset(presetName) {
        const preset = SOUND_PRESETS[presetName];
        if (!preset) return;

        this.presetName = presetName;
        this.preset = preset;

        if (this.masterFilter) {
            this.masterFilter.frequency.value = preset.filterFreq;
        }

        this.saveSettings();
        log(`[Sound] Preset set to: ${presetName}`);
    }

    /**
     * Get current preset name
     * @returns {string}
     */
    getPreset() {
        return this.presetName;
    }

    /**
     * Save settings to localStorage
     */
    saveSettings() {
        try {
            const settings = {
                enabled: this.enabled,
                volume: this.volume,
                preset: this.presetName
            };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
        } catch (err) {
            console.warn('[Sound] Could not save settings:', err);
        }
    }

    /**
     * Load settings from localStorage
     */
    loadSettings() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            if (data) {
                const settings = JSON.parse(data);
                this.enabled = settings.enabled ?? true;
                this.volume = settings.volume ?? 0.5;
                if (settings.preset && SOUND_PRESETS[settings.preset]) {
                    this.presetName = settings.preset;
                    this.preset = SOUND_PRESETS[settings.preset];
                }
            }
        } catch (err) {
            console.warn('[Sound] Could not load settings:', err);
        }
    }

    /**
     * Plays a short buzzer/bonk sound for invalid moves using Web Audio synthesis.
     * Two slightly dissonant oscillators (150Hz + 160Hz) with quick 100ms decay.
     */
    playInvalidMove() {
        if (!this.enabled || !this.initialized || !this.audioContext) {
            return;
        }

        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        const ctx = this.audioContext;
        const now = ctx.currentTime;

        try {
            // Oscillator 1: 150Hz low tone
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = this.preset.waveform || 'square';
            osc1.frequency.value = 150;
            gain1.gain.setValueAtTime(0.3, now);
            gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
            osc1.connect(gain1);
            gain1.connect(this.masterGain);
            osc1.start(now);
            osc1.stop(now + 0.1);

            // Oscillator 2: 160Hz slightly dissonant
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = this.preset.waveform || 'square';
            osc2.frequency.value = 160;
            gain2.gain.setValueAtTime(0.25, now);
            gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
            osc2.connect(gain2);
            gain2.connect(this.masterGain);
            osc2.start(now);
            osc2.stop(now + 0.1);
        } catch (err) {
            console.warn('[Sound] Invalid move synthesis failed:', err.message);
        }
    }

    /**
     * Plays a short tick/click sound for turn timer countdown using Web Audio synthesis.
     * 50ms 800Hz sine wave with quick decay.
     */
    playTimerTick() {
        if (!this.enabled || !this.initialized || !this.audioContext) {
            return;
        }

        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        const ctx = this.audioContext;
        const now = ctx.currentTime;

        try {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = this.preset.waveform || 'sine';
            osc.frequency.value = 800;
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
            osc.connect(gain);
            gain.connect(this.masterGain);
            osc.start(now);
            osc.stop(now + 0.05);
        } catch (err) {
            console.warn('[Sound] Timer tick synthesis failed:', err.message);
        }
    }

    /**
     * Plays an elaborate victory fanfare using Web Audio synthesis.
     * Rising frequency sweep with harmony layer and reverb decay (~3s total).
     * Falls back to the preloaded 'victory' sound if AudioContext unavailable.
     */
    playVictoryFanfare() {
        if (!this.enabled || !this.initialized || !this.audioContext) {
            return;
        }

        // Resume context if suspended
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        const ctx = this.audioContext;
        const now = ctx.currentTime;

        try {
            // Create convolver for reverb-like decay
            const reverbGain = ctx.createGain();
            reverbGain.gain.setValueAtTime(0.4 * this.volume, now);
            reverbGain.gain.exponentialRampToValueAtTime(0.001, now + 3.0);
            reverbGain.connect(this.masterGain);

            // Layer 1: Rising sweep 200Hz -> 800Hz over 1s
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(200, now);
            osc1.frequency.exponentialRampToValueAtTime(800, now + 1.0);
            gain1.gain.setValueAtTime(0.3, now);
            gain1.gain.setValueAtTime(0.3, now + 0.9);
            gain1.gain.exponentialRampToValueAtTime(0.001, now + 2.5);
            osc1.connect(gain1);
            gain1.connect(reverbGain);
            osc1.start(now);
            osc1.stop(now + 2.5);

            // Layer 2: Harmony sweep 300Hz -> 1200Hz
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(300, now + 0.05);
            osc2.frequency.exponentialRampToValueAtTime(1200, now + 1.05);
            gain2.gain.setValueAtTime(0.2, now + 0.05);
            gain2.gain.setValueAtTime(0.2, now + 0.95);
            gain2.gain.exponentialRampToValueAtTime(0.001, now + 2.5);
            osc2.connect(gain2);
            gain2.connect(reverbGain);
            osc2.start(now + 0.05);
            osc2.stop(now + 2.5);

            // Layer 3: Triumphant staccato notes (C5 E5 G5 C6)
            const notes = [523, 659, 784, 1047];
            notes.forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.value = freq;
                const noteStart = now + 1.0 + i * 0.15;
                gain.gain.setValueAtTime(0, noteStart);
                gain.gain.linearRampToValueAtTime(0.25, noteStart + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.001, noteStart + 0.8);
                osc.connect(gain);
                gain.connect(reverbGain);
                osc.start(noteStart);
                osc.stop(noteStart + 0.8);
            });

            // Also play the preloaded victory sound for layered richness
            this.play('victory', { volume: 0.7 });
        } catch (err) {
            // Fallback to simple victory sound
            console.warn('[Sound] Victory fanfare synthesis failed, using fallback:', err.message);
            this.play('victory');
        }
    }

    /**
     * Cleanup and release resources
     */
    dispose() {
        this.stopAll();
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.sounds.clear();
        this.initialized = false;
    }
}

// Global singleton instance
let soundManagerInstance = null;

/**
 * Get the global SoundManager instance
 * @returns {SoundManager}
 */
export function getSoundManager() {
    if (!soundManagerInstance) {
        soundManagerInstance = new SoundManager();
    }
    return soundManagerInstance;
}

export default SoundManager;
